import { createFileRoute } from '@tanstack/react-router';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Activity, CheckCircle2, Target, AlertCircle, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';

export const Route = createFileRoute('/dashboard/')({
  component: DashboardIndex,
});

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  loading?: boolean;
}

function StatCard({ title, value, description, icon, trend, loading }: StatCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {icon}
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-24 mb-2" />
          {description && <Skeleton className="h-4 w-32" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        {trend && (
          <div className="flex items-center mt-2">
            <TrendingUp
              className={`h-4 w-4 mr-1 ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}
            />
            <span
              className={`text-xs font-medium ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}
            >
              {trend.value}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardIndex() {
  const { data: pickems = [], isLoading: pickemsLoading } = useGetPickemsQuery();

  const stats = useMemo(() => {
    const now = new Date();

    const activePickems = pickems.filter((p: any) => {
      const status = p.status || p.pickem_status;
      const startDate = p.start_date || p.start_utc || p.startUtc;
      const endDate = p.end_date || p.end_utc;

      if (status === 'active' || status === 'ACTIVE' || status === '1' || status === 1) {
        if (startDate && endDate) {
          return new Date(startDate) <= now && new Date(endDate) >= now;
        }
        return true;
      }
      return false;
    });

    const completedPickems = pickems.filter((p: any) => {
      const status = p.status || p.pickem_status;
      return status === 'complete' || status === 'COMPLETE' || status === 'completed';
    });

    const unscoredPickems = completedPickems.filter((p: any) => !p.scored_at);

    const totalPickems = pickems.length;

    return {
      activePickems: activePickems.length,
      completedPickems: completedPickems.length,
      totalPickems,
      unscoredPickems,
    };
  }, [pickems]);

  const isLoading = pickemsLoading;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your Pickem application performance</p>
      </div>

      <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
        <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <AlertTitle className="text-amber-900 dark:text-amber-300">Work In Progress</AlertTitle>
        <AlertDescription className="text-amber-800 dark:text-amber-400">
          This dashboard is currently under development. The numbers and statistics shown may not be
          accurate. Please do not rely on this data for production use.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Active Pickems"
          value={stats.activePickems}
          description="Currently running"
          icon={<Activity className="h-4 w-4 text-muted-foreground" />}
          loading={isLoading}
        />
        <StatCard
          title="Completed Pickems"
          value={stats.completedPickems}
          description="Finished competitions"
          icon={<CheckCircle2 className="h-4 w-4 text-muted-foreground" />}
          loading={isLoading}
        />
        <StatCard
          title="Total Pickems"
          value={stats.totalPickems}
          description="All pickems"
          icon={<Target className="h-4 w-4 text-muted-foreground" />}
          loading={isLoading}
        />
      </div>

      {stats.unscoredPickems.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unscored Pickems</AlertTitle>
          <AlertDescription>
            <div className="mt-2 space-y-2">
              <p className="text-sm">
                {stats.unscoredPickems.length} pickem{stats.unscoredPickems.length !== 1 ? 's' : ''}{' '}
                completed but not scored:
              </p>
              <div className="flex flex-wrap gap-2">
                {stats.unscoredPickems.slice(0, 5).map((pickem: any, index: number) => (
                  <Badge key={pickem.pickem_id || pickem.id || index} variant="warning">
                    {pickem.title_en ||
                      pickem.title_fr ||
                      pickem.name_en ||
                      pickem.name_lt ||
                      'Unnamed'}
                  </Badge>
                ))}
                {stats.unscoredPickems.length > 5 && (
                  <Badge variant="outline">+{stats.unscoredPickems.length - 5} more</Badge>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
